import React, { useState, useRef } from "react";
import axios from "axios";
import { Camera, ArrowLeft, Save } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useModal } from "../context/ModalContext";

const apiUrl = process.env.REACT_APP_API_URL;

export default function EditProfilePage() {
  const { showAlert } = useModal();
  const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
  const storedUser = uStr ? JSON.parse(uStr) : null;

  const [name, setName] = useState(storedUser?.name || "");
  const [phone, setPhone] = useState(storedUser?.phone || "");
  const [picture, setPicture] = useState(storedUser?.picture || "");
  const [bio, setBio] = useState(storedUser?.bio || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleSave = async () => {
    if (!name || !name.trim()) {
      showAlert("Full name is required.", "error");
      return;
    }

    if (!phone || !phone.trim()) {
      showAlert("Phone number is required.", "error");
      return;
    }

    const phoneRegex = /^(\+251|251|0)[1-9][0-9]{8}$/;
    const cleanedPhone = phone.replace(/\s/g, "");
    if (!phoneRegex.test(cleanedPhone)) {
      showAlert("Please enter a valid phone number (e.g. 0912345678 or +251912345678).", "error");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("phone", cleanedPhone);
      formData.append("bio", bio);
      formData.append("picture", picture); // Keep existing if no new file
      if (file) {
        formData.append("profilePic", file);
      }

      const res = await axios.put(
        `${apiUrl}/user/update`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          } 
        }
      );

      // Update storage with new data
      if (localStorage.getItem("user")) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
      } else {
        sessionStorage.setItem("user", JSON.stringify(res.data.user));
      }

      showAlert("Profile updated!", "success");
      setTimeout(() => {
        window.location.href = "/profile";
      }, 1500);
    } catch (err) {
      console.error(err);
      showAlert(err.response?.data?.error || "Error updating profile.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header Decor */}
          <div className="h-32 bg-gradient-to-r from-green-600 to-emerald-500 relative">
            <button 
              onClick={() => window.location.href = "/profile"}
              className="absolute top-6 left-6 p-2 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/30 transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>

          <div className="px-8 pb-10 -mt-12 relative">
            {/* Profile Picture Section */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative group">
                <img
                  src={preview || picture || `https://ui-avatars.com/api/?name=${name}`}
                  alt="avatar"
                  className="w-32 h-32 rounded-full border-4 border-white shadow-2xl object-cover"
                />
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="absolute bottom-1 right-1 p-2.5 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition transform hover:scale-110"
                  title="Change Photo"
                >
                  <Camera className="w-5 h-5" />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
              </div>
              <h1 className="mt-4 text-2xl font-bold text-gray-900">Edit Profile</h1>
              <p className="text-gray-500 text-sm">Update your personal information</p>
            </div>

            <div className="space-y-6">
              {/* Name */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 ml-1">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:bg-white outline-none transition"
                  placeholder="Your full name"
                />
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 ml-1 flex items-center justify-between">
                  <span>Phone Number <span className="text-red-500">*</span></span>
                  <span className="text-xs text-gray-400 font-normal">Required for buyer contacts</span>
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:bg-white outline-none transition"
                  placeholder="09XXXXXXXX or +2519XXXXXXXX"
                />
              </div>

              {/* Bio */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 ml-1">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:bg-white outline-none transition h-32 resize-none"
                  placeholder="Write a short bio about yourself..."
                ></textarea>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-green-600 text-white rounded-2xl font-bold shadow-lg shadow-green-200 hover:bg-green-700 hover:shadow-green-300 transition disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {loading ? "Saving Changes..." : "Save Profile"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
